import React from "react";
import { Link } from "react-router-dom";
import { Download, ArrowRight, ChevronRight } from "lucide-react";
import Layout from "../components/Layout";
import Seo from "../seo/Seo";
import { programGraph, faqGraph } from "../seo/jsonld";
import { program as programMock } from "../mock";
import { programMeta, programIntro, chapters } from "../programData";
import PdfViewer from "../components/PdfViewer";

const PDF_URL = programMock.pdfUrl;

const faqItems = [
  {
    q: "Kto je Ján Kuráň?",
    a: "Ján Kuráň je Ružomberčan a kandidát na primátora mesta Ružomberok v komunálnych voľbách 2026 s volebným číslom 3.",
  },
  {
    q: "Koĺko programových oblastí má program Jána Kuráňa?",
    a: "Volebný program je rozdelený do 20 oblastí – od práce a bývania cez dopravu a školstvo až po klímu a fungovanie mesta.",
  },
  {
    q: "Čo navrhuje v oblasti bývania?",
    a: "Vytvoriť podmienky na vznik približne 1 000 nových bytov do roku 2036 kombináciou družstevného, nájomného a súkromného bývania.",
  },
  {
    q: "Čo navrhuje pre pracovné miesta a investorov?",
    a: "Vytvoriť mestský tím pre investorov, databázu pozemkov a hál a podmienky pre vznik tisícov pracovných príležitostí v horizonte desaťročia.",
  },
  {
    q: "Čo navrhuje pre MHD?",
    a: "Cieľom je 100 % dotácia mesta na MHD, optimalizácia liniek podľa potrieb ľudí a dôstojné moderné zastávky.",
  },
  {
    q: "Kde si môžem stiahnuť celý program?",
    a: "Celý program je dostupný ako HTML na tejto stránke a tiež na stiahnutie vo formáte PDF.",
  },
];

const ProgramPage = () => {
  return (
    <Layout>
      <Seo
        title={programMeta.title}
        description={programMeta.description}
        path="/program"
        jsonLd={[
          programGraph({ path: "/program", title: programMeta.title, description: programMeta.description }),
          faqGraph(faqItems),
        ]}
      />

      {/* Hlavička programu */}
      <section className="bg-campaign-blue px-4 sm:px-6 py-14 sm:py-20">
        <div className="max-w-4xl mx-auto">
          <nav aria-label="Omrvinková navigácia" className="text-campaign-cream/70 text-sm font-semibold mb-6 flex items-center gap-1 flex-wrap">
            <Link to="/" className="hover:text-campaign-yellow">Domov</Link>
            <ChevronRight size={14} />
            <span className="text-campaign-yellow">Program</span>
          </nav>
          <h1 className="font-display text-4xl sm:text-6xl text-campaign-cream leading-none uppercase">
            Volebný program
          </h1>
          <p className="mt-4 font-display text-campaign-yellow text-lg sm:text-2xl uppercase">
            {programMeta.lead}
          </p>
          <p className="mt-2 text-campaign-cream/85 text-base sm:text-lg">{programMeta.subLead}</p>
          <p className="mt-6 text-campaign-cream/90">
            Ján Kuráň kandiduje na primátora mesta Ružomberok v komunálnych voľbách 2026.
            Jeho volebný program je rozdelený do 20 hlavných oblastí.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-campaign-yellow text-campaign-ink font-display uppercase text-sm sm:text-base px-5 py-4 border-2 border-campaign-ink shadow-brutal hover-lift"
            >
              <Download size={20} /> Stiahnuť program (PDF)
            </a>
            <PdfViewer url={PDF_URL} fileName={programMock.pdfFileName} />
          </div>
        </div>
      </section>

      {/* úvod / smer */}
      <section className="bg-campaign-cream px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-2xl sm:text-4xl text-campaign-ink uppercase mb-6">
            {programIntro.heading}
          </h2>
          {programIntro.paragraphs.map((p, i) => (
            <p key={i} className="text-campaign-ink/85 leading-relaxed mb-4">{p}</p>
          ))}
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {programIntro.goals.map((g, i) => (
              <div key={i} className="bg-campaign-yellow border-4 border-campaign-ink p-5">
                <h3 className="font-display text-campaign-ink uppercase mb-2">{g.title}</h3>
                <p className="text-campaign-ink/85 text-sm">{g.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-campaign-ink/70 text-sm italic">{programIntro.note}</p>
        </div>
      </section>

      {/* Obsah / rozcestník */}
      <section className="bg-campaign-yellow border-y-4 border-campaign-ink px-4 sm:px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl text-campaign-ink uppercase mb-6">Obsah programu</h2>
          <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2 list-none">
            {chapters.map((c) => (
              <li key={c.slug} className="flex items-baseline gap-3">
                <span className="font-display text-campaign-ink/40">{c.no}</span>
                <Link to={`/program/${c.slug}`} className="font-semibold text-campaign-ink hover:text-campaign-blue underline decoration-campaign-ink/20">
                  {c.title}
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Jednotlivé kapitoly (plný HTML text) */}
      <div className="bg-campaign-cream px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto space-y-12">
          {chapters.map((c) => (
            <article key={c.slug} id={c.slug} className="scroll-mt-24 border-b-2 border-campaign-ink/10 pb-10">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-display text-3xl text-campaign-coral">{c.no}</span>
                <h2 className="font-display text-2xl sm:text-4xl text-campaign-ink uppercase leading-tight">
                  {c.title}
                </h2>
              </div>
              {c.tagline && <p className="font-semibold text-campaign-blue mb-3">{c.tagline}</p>}
              {c.intro && <p className="text-campaign-ink/85 leading-relaxed mb-4">{c.intro}</p>}

              {c.sections.map((s, i) => (
                <div key={i} className="mt-5">
                  <h3 className="font-display text-lg text-campaign-ink uppercase mb-2">{s.h}</h3>
                  <ul className="space-y-1.5 list-disc pl-5">
                    {s.items.map((it, j) => (
                      <li key={j} className="text-campaign-ink/85 leading-snug">{it}</li>
                    ))}
                  </ul>
                </div>
              ))}

              {c.benefits && c.benefits.length > 0 && (
                <div className="mt-5 bg-campaign-blue border-4 border-campaign-ink p-5">
                  <h3 className="font-display text-campaign-yellow uppercase mb-2">Čo tým získa obyvateľ?</h3>
                  <ul className="space-y-1.5 list-disc pl-5">
                    {c.benefits.map((b, k) => (
                      <li key={k} className="text-campaign-cream/90 leading-snug">{b}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Link to={`/program/${c.slug}`} className="mt-4 inline-flex items-center gap-1 font-semibold text-campaign-blue hover:underline">
                Detail oblasti: {c.title} <ArrowRight size={16} />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default ProgramPage;
